import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useLanguage } from "@/contexts/LanguageContext";
import { Menu } from "lucide-react";
import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import logo from "@/assets/khaled-logo.svg";

export const Navigation = () => {
  const location = useLocation();
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const isActive = (path: string) => location.pathname === path;

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      setScrolled(currentScrollY > 20);
      
      // Hide navbar when scrolling down, show when scrolling up
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setHidden(true);
      } else {
        setHidden(false);
      }
      
      lastScrollY = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav aria-label="Primary navigation" className={`fixed top-0 left-0 right-0 z-50 border-b transition-all duration-300 ${hidden ? '-translate-y-full' : 'translate-y-0'} ${scrolled ? 'bg-background/90 backdrop-blur-md py-2 border-border' : 'bg-transparent backdrop-blur-none py-4 border-transparent'}`} style={scrolled ? { boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.15), 0 2px 4px -1px rgba(59, 130, 246, 0.1)' } : {}}>
      <div className="w-full px-6 lg:px-12 xl:px-16 flex items-center justify-between">
        <Link to="/" aria-label="Khaled Kasmou — home" className="flex items-center hover:scale-105 transition-transform duration-300 focus-premium">
          <img src={logo} alt="Khaled Kasmou" className={`w-auto transition-all duration-300 ${scrolled ? 'h-8' : 'h-10'}`} />
        </Link>
        
        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-6">
          <Link 
            to="/" 
            aria-current={isActive('/') ? 'page' : undefined}
            className={`text-sm relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-full after:origin-left after:scale-x-0 after:bg-foreground after:transition-transform after:duration-300 hover:after:scale-x-100 ${isActive('/') ? 'text-foreground font-medium after:scale-x-100' : 'text-muted-foreground hover:text-foreground'} transition-colors`}
          >
            {t("nav.work")}
          </Link>
          <Link 
            to="/about" 
            aria-current={isActive('/about') ? 'page' : undefined}
            className={`text-sm relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-full after:origin-left after:scale-x-0 after:bg-foreground after:transition-transform after:duration-300 hover:after:scale-x-100 ${isActive('/about') ? 'text-foreground font-medium after:scale-x-100' : 'text-muted-foreground hover:text-foreground'} transition-colors`}
          >
            {t("nav.about")}
          </Link>
          <Link
            to="/cv"
            aria-current={isActive('/cv') ? 'page' : undefined}
            className={`text-sm relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-full after:origin-left after:scale-x-0 after:bg-foreground after:transition-transform after:duration-300 hover:after:scale-x-100 ${isActive('/cv') ? 'text-foreground font-medium after:scale-x-100' : 'text-muted-foreground hover:text-foreground'} transition-colors`}
          >
            {t("nav.cv")}
          </Link>
          <Link
            to="/portfolio-review"
            aria-current={isActive('/portfolio-review') ? 'page' : undefined}
            className={`text-sm relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-full after:origin-left after:scale-x-0 after:bg-foreground after:transition-transform after:duration-300 hover:after:scale-x-100 ${isActive('/portfolio-review') ? 'text-foreground font-medium after:scale-x-100' : 'text-muted-foreground hover:text-foreground'} transition-colors`}
          >
            {t("nav.portfolioReview")}
          </Link>

          <div className="h-4 w-px bg-border mx-2" />
          
          <LanguageToggle />
          
          <Button 
            asChild
            variant="outline"
            size="sm"
            className="ml-2"
          >
            <Link to="/contact" aria-current={isActive('/contact') ? 'page' : undefined}>
              {t("nav.contact")}
            </Link>
          </Button>
        </div>

        {/* Mobile/Tablet Burger Menu */}
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="ghost" size="icon" aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}>
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[300px] bg-background/95 backdrop-blur-md animate-in slide-in-from-right duration-300">
            <div className="flex flex-col gap-6 mt-8">
              <Link 
                to="/" 
                aria-current={isActive('/') ? 'page' : undefined}
                onClick={() => setIsOpen(false)}
                className={`text-lg hover:translate-x-1 ${isActive('/') ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'} transition-all duration-300`}
              >
                {t("nav.work")}
              </Link>
              <Link 
                to="/about" 
                aria-current={isActive('/about') ? 'page' : undefined}
                onClick={() => setIsOpen(false)}
                className={`text-lg hover:translate-x-1 ${isActive('/about') ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'} transition-all duration-300`}
              >
                {t("nav.about")}
              </Link>
              <Link
                to="/cv"
                aria-current={isActive('/cv') ? 'page' : undefined}
                onClick={() => setIsOpen(false)}
                className={`text-lg hover:translate-x-1 ${isActive('/cv') ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'} transition-all duration-300`}
              >
                {t("nav.cv")}
              </Link>
              <Link
                to="/portfolio-review"
                aria-current={isActive('/portfolio-review') ? 'page' : undefined}
                onClick={() => setIsOpen(false)}
                className={`text-lg hover:translate-x-1 ${isActive('/portfolio-review') ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'} transition-all duration-300`}
              >
                {t("nav.portfolioReview")}
              </Link>

              <div className="h-px bg-border my-2" />
              
              <LanguageToggle />
              
              <Button 
                asChild
                variant="outline"
                className="border-foreground text-foreground hover:bg-foreground hover:text-background hover:scale-105 transition-all duration-300"
              >
                <Link to="/contact" aria-current={isActive('/contact') ? 'page' : undefined} onClick={() => setIsOpen(false)}>
                  {t("nav.contact")}
                </Link>
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
};
